import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';

type Loader<T> = (signal: AbortSignal) => Promise<T>;

export interface UseApiResult<T> {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Generic GET hook. The loader closure is supplied by the caller so we
 * stay agnostic of endpoint shape. Caller is responsible for memoizing
 * the loader (or passing a stable function reference) — useApi re-runs
 * whenever `loader` changes.
 */
export function useApi<T>(loader: Loader<T>): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const aborterRef = useRef<AbortController | null>(null);

  useEffect(() => {
    aborterRef.current?.abort();
    const ac = new AbortController();
    aborterRef.current = ac;
    setLoading(true);
    setError(null);
    loader(ac.signal)
      .then((d) => {
        if (!ac.signal.aborted) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
    return () => ac.abort();
  }, [loader, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { data, error, loading, refetch };
}
